/* ══════════════════════════════════════════════════════════════════════════
 * THE APP CHECKS IT IS THE APP THAT IS CURRENTLY PUBLISHED
 *
 * Al, 23 September 2026, twice: *"right now, the app doesn't open up on my
 * phone… nothing's happening"* and then, after a fix and a deploy, *"same
 * thing."*  His phone showed a real JavaScript fault — "Cannot access 'a'
 * before initialization". The build it came from had already been replaced by
 * the next deploy, and every build I could reach rendered every screen
 * correctly, signed out AND signed in.
 *
 * ── WHAT IS LEFT WHEN THE SERVER IS PROVED INNOCENT ────────────────────
 *
 * Vite gives every build a content hash: `index-NPEV4ezt.js`. `index.html`
 * points at it. **A browser holding an older `index.html` asks for an older
 * hash**, and what comes back depends on the host — the old file if it is
 * still there, the SPA fallback (HTML) if it is not. A browser handed HTML
 * where it expected a module, or half of an older module graph beside newer
 * cached pieces, throws exactly this shape of error and renders nothing.
 *
 * It is the oldest bug in single-page apps and it has one honest property:
 * **it only ever happens to the person who had the app open before the
 * deploy.** Which is always Al, on a phone with the tab still open, every
 * single time he is asked to push.
 *
 * ── SO THE APP ASKS WHETHER IT IS STALE, AND HEALS ITSELF ──────────────
 *
 * On boot it fetches `/` with `cache: 'no-store'` — deliberately going past
 * whatever the browser is holding — and reads the script name the server says
 * is current. If that is not the script actually running, the page is old and
 * reloads once.
 *
 * ⚠️ ONCE. NOT UNTIL IT AGREES. A reload that re-checks and reloads again is a
 * loop that bricks the app harder than the fault it is fixing — on a phone,
 * with no way out but clearing the browser. The hash it reloaded FOR is
 * written to sessionStorage first, so a second disagreement about the same
 * hash is a real problem and is left alone for the boundary to report.
 *
 * ⚠️ AND IT NEVER BLOCKS THE FIRST PAINT. The check is fired and forgotten; if
 * the network is slow, or offline, or the answer is unreadable, the app has
 * already drawn and this simply does nothing. A freshness check that can stop
 * the app from starting is a worse fault than the one it prevents.
 * ══════════════════════════════════════════════════════════════════════════ */

const MARK = 'eleat.reloadedFor';

/** The bundle actually running, read off the script tag the browser loaded. */
export function runningBuild() {
  try {
    const src = [...document.querySelectorAll('script[src]')]
      .map((n) => n.getAttribute('src'))
      .find((v) => v && /assets\/index-[\w-]+\.js/.test(v));
    return src ? (src.match(/index-([\w-]+)\.js/) || [])[1] || null : null;
  } catch { return null; }
}

/** The bundle the server says is current, fetched past every cache. */
async function publishedBuild() {
  const res = await fetch('/', { cache: 'no-store' });
  if (!res.ok) return null;
  const html = await res.text();
  return (html.match(/assets\/index-([\w-]+)\.js/) || [])[1] || null;
}

/* ══════════════════════════════════════════════════════════════════════════
 * ⚠️⚠️ AND IT RUNS AGAIN WHEN THE TAB COMES BACK — 23 SEPTEMBER 2026
 *
 * Al's fourth screenshot settled what four days of theorising did not. It read
 * **`build QKtMYe6Q`** — and the server was serving `index-CfJu17vd.js`. His
 * phone was running a build two deploys old, and the freshness check, which is
 * INSIDE that build, had not fired.
 *
 * Because it only ever ran once, at module evaluation. **Android Chrome does
 * not re-evaluate a module when you return to a tab** — his screenshot shows
 * nineteen open tabs, and coming back to one restores the page from the
 * back-forward cache exactly as it was. No script runs. No check happens. The
 * page can sit there for days, two deploys behind, and every "reload" that is
 * really a tab switch changes nothing.
 *
 * So the check is bound to `pageshow` as well, which is the one event that
 * fires for a restored page — `persisted` is true precisely then.
 *
 * ⚠️ AND ALSO ON `visibilitychange`, because a phone that has been in a pocket
 * comes back through that and not always through pageshow.
 * ══════════════════════════════════════════════════════════════════════════ */
export function watchForNewBuilds() {
  const again = () => { reloadIfStale().catch(() => {}); };
  try {
    window.addEventListener('pageshow', (e) => { if (e.persisted) again(); });
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') again();
    });
  } catch { /* an app that cannot listen still works */ }
}

export async function reloadIfStale() {
  const running = runningBuild();
  if (!running) return;                 /* dev server: no hashed bundle */

  let published = null;
  try { published = await publishedBuild(); } catch { return; }
  if (!published || published === running) return;

  /* ⚠️ THE GUARD, AND IT IS WRITTEN BEFORE THE RELOAD, NOT AFTER. Writing it
   * afterwards never happens — the page is already gone. */
  let already = null;
  try { already = sessionStorage.getItem(MARK); } catch { /* no storage */ }
  if (already === published) return;    /* tried that already; do not loop */

  try { sessionStorage.setItem(MARK, published); } catch { /* fine */ }

  // eslint-disable-next-line no-console
  console.warn(`[eleat] this page is build ${running}; the server is serving `
    + `${published}. Reloading once.`);

  /* ⚠️ `replace`, NOT `assign`. A reload that pushes a history entry leaves
   * the Eleater's back button pointing at the broken copy. */
  window.location.replace(window.location.href);
}
